import gsap from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'

import Site from 'stores/Site'

import Hero from 'components/Hero'
import Lottie from 'components/Lottie'
import Slider from 'components/Slider'
import Faq from 'components/Faq'
import Tabs from 'components/Tabs'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Posts from 'components/Posts'

export default class Page {
    constructor() {
        this.classes = []
    }

    init() {
        this.initHero()
        this.initRegionLinks()
        this.initChat()
        this.initBgs()
        this.initLotties()
        this.initSliders()
        this.initFaqs()
        this.initTabs()
        this.initTables()
        this.initModals()
        this.initPosts()
    }

    initChat() {
        const btns = Site.page.querySelectorAll('.btn-cta-chat')

        window.hsConversationsOnReady = [
            () => {
                btns.forEach(btn => {
                    btn.addEventListener('click', (e)=> {
                        e.preventDefault()

                        window.HubSpotConversations.widget.open()
                    })
                })
            }
        ]
    }

    initRegionLinks() {
        const links = Site.page.querySelectorAll('.region-link')

        links.forEach(link => {
            link.classList.add('active')

            link.addEventListener('click', async (e) => {
                e.preventDefault()

                const url    = link.getAttribute('href')
                const region = link.getAttribute('data-region')

                const ajaxurl = window.script_params && window.script_params.ajaxurl

                // Helper to fall back to your GET + template_redirect flow
                const fallbackNavigate = () => {
                   window.location.href = url
                }

                // If ajaxurl missing, bail to fallback immediately
                if (!ajaxurl) return fallbackNavigate()

                try {
                    const body = new URLSearchParams({
                        action: 'zlk_change_region',
                        region,
                        url
                    })

                    const res = await fetch(ajaxurl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        credentials: 'same-origin',
                        body
                    })

                    // If server didn’t return JSON, treat as failure and fallback
                    const data = await res.json().catch(() => null)

                    if (data?.success && data?.data?.redirect) {
                        window.location.replace(data.data.redirect)
                    } else {
                        fallbackNavigate()
                    }
                } catch (err) {
                    // Network/JS error: just use the GET flow
                    fallbackNavigate()
                }
            })
        })
    }

    initBgs() {
        if (Site.isMobile) return

        gsap.registerPlugin(ScrollTrigger)

        const els = Site.page.querySelectorAll('.s-bg')

        els.forEach(el => {
            const section = el.closest('.section')
            const paths = el.querySelectorAll('path')

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top bottom',
                    end: 'bottom center',
                    scrub: true
                }
            })

            paths.forEach((path, i) => {
                tl.fromTo(path, {
                    yPercent: 10 * i + 10
                }, {
                    yPercent: 0
                }, 0)
            })
        })
    }

    initHero() {
        const el = Site.page.querySelector('.s-hero')

        if (el) {
            new Hero(el)
        }
    }

    initPosts() {
        const el = Site.page.querySelector('.posts')

        if (el) {
            new Posts(el)
        }
    }

    initLotties() {
        const els = Site.page.querySelectorAll('.lottie')

        els.forEach(el => {
            new Lottie(el)
        })
    }

    initFaqs() {
        const els = Site.page.querySelectorAll('.s-faq')

        els.forEach(el => {
            new Faq(el)
        })
    }

    initTabs() {
        const els = Site.page.querySelectorAll('.tabs')

        els.forEach(el => {
            new Tabs(el)
        })
    }

    initTables() {
        if (Site.isMobile) return

        const els = Site.page.querySelectorAll('.comparison-table')

        els.forEach(el => {
            new Table(el)
        })
    }

    initModals() {
        const modals = []

        const els = document.querySelectorAll('.modal')
        const links = document.querySelectorAll('[data-modal]')

        if (els.length > 0) {
            els.forEach(el => {
                const id = el.getAttribute('data-id')

                modals[id] = new Modal(el)
            })

            links.forEach(link => {
                const id = link.getAttribute('data-modal')

                link.addEventListener('click', () => {
                    modals[id].toggle()
                })
            })
        }
    }

    initSliders() {
        const wrappers = Site.page.querySelectorAll('.slider')

        wrappers.forEach(wrapper => {
            let amount = 3,
                amountMobile =  1,
                amountTablet = 1,
                gap = 32,
                centeredSlides = true,
                autoplay = false,
                disabledMobile = false,
                disabledDesktop = false

            if (wrapper.getAttribute('data-swiper') !== null) {
                const data = JSON.parse(wrapper.getAttribute('data-swiper'))

                if (data.amount) {
                    amount = data.amount
                }

                if (data.amountTablet) {
                    amountTablet = data.amountTablet
                }

                if (data.amountMobile) {
                    amountMobile = data.amountMobile
                }

                if (data.disabledMobile) {
                    disabledMobile = data.disabledMobile
                }

                if (data.disabledDesktop) {
                    disabledDesktop = data.disabledDesktop
                }

                if (data.gap) {
                    gap = data.gap
                }

                if (data.autoplay) {
                    autoplay = data.autoplay
                }
            }

            if (amount % 2 === 0) {
                centeredSlides = false
            }

            new Slider({wrapper, amount, disabledMobile, disabledDesktop, amountMobile, amountTablet, gap, centeredSlides, autoplay})
        })
    }

    onResize() {
        this.classes.forEach(instance => {
            if (instance.onResize) {
                instance.onResize()
            }
        })
    }

    onScroll() {
        this.classes.forEach(instance => {
            if (instance.onScroll) {
                instance.onScroll()
            }
        })
    }
}